import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RotatingDeleteIcon } from '../RotatingDeleteIcon';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_FIELD_LABEL_CLASSNAME,
  ADMIN_PAGE_NARROW_CLASSNAME,
  ADMIN_PANEL_CLASSNAME,
  ADMIN_PANEL_TITLE_CLASSNAME,
  ADMIN_TABLE_CELL_MUTED_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminTableWrap,
} from './admin-ui';

interface InvitationCode {
  code: string;
  remainingRedeems: number;
  grantsAdmin: boolean;
  createdAt: string;
}

export const InvitationCodes: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [codes, setCodes] = useState<InvitationCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newRedeems, setNewRedeems] = useState(10);
  const [newGrantsAdmin, setNewGrantsAdmin] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editRedeems, setEditRedeems] = useState(0);
  const [editGrantsAdmin, setEditGrantsAdmin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchCodes = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/invitation-codes?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/invitation-codes`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch invitation codes');
        }

        const data = await response.json();
        setCodes(data.codes || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching invitation codes:', err);
        setError(t('failed_to_load_invitation_codes'));
      } finally {
        setLoading(false);
      }
    };

    fetchCodes();
  }, [token, user, masterKey, t]);

  const handleCreate = async () => {
    if (!newCode.trim() || submitting) return;

    try {
      setSubmitting(true);
      const url = masterKey
        ? `${API_BASE}/api/invitation-codes?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/invitation-codes`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          code: newCode,
          remainingRedeems: newRedeems,
          grantsAdmin: newGrantsAdmin,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create invitation code');
      }

      const data = await response.json();
      setCodes([data.code, ...codes]);
      setNewCode('');
      setNewRedeems(10);
      setNewGrantsAdmin(false);
      setShowCreateForm(false);
      setError(null);
    } catch (err: any) {
      console.error('Error creating invitation code:', err);
      setError(err.message || t('failed_to_create_invitation_code'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (code: string) => {
    if (submitting) return;

    try {
      setSubmitting(true);
      const url = masterKey
        ? `${API_BASE}/api/invitation-codes/${encodeURIComponent(code)}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/invitation-codes/${encodeURIComponent(code)}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          remainingRedeems: editRedeems,
          grantsAdmin: editGrantsAdmin,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update invitation code');
      }

      const data = await response.json();
      setCodes(codes.map((c) => (c.code === code ? data.code : c)));
      setEditingCode(null);
      setError(null);
    } catch (err) {
      console.error('Error updating invitation code:', err);
      setError(t('failed_to_update_invitation_code'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (code: string) => {
    if (submitting || !confirm(t('confirm_delete_invitation_code'))) return;

    try {
      setSubmitting(true);
      const url = masterKey
        ? `${API_BASE}/api/invitation-codes/${encodeURIComponent(code)}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/invitation-codes/${encodeURIComponent(code)}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete invitation code');
      }

      setCodes(codes.filter((c) => c.code !== code));
      setError(null);
    } catch (err) {
      console.error('Error deleting invitation code:', err);
      setError(t('failed_to_delete_invitation_code'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (code: InvitationCode) => {
    setEditingCode(code.code);
    setEditRedeems(code.remainingRedeems);
    setEditGrantsAdmin(code.grantsAdmin);
  };

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  return (
    <>
      <div className={ADMIN_PAGE_NARROW_CLASSNAME}>
        <div className="mb-8 flex items-center justify-end">
          <Button onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? t('cancel') : t('create_invitation_code')}
          </Button>
        </div>

        {error && (
          <div className="mb-4">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        {showCreateForm && (
          <div className={ADMIN_PANEL_CLASSNAME}>
            <h2 className={ADMIN_PANEL_TITLE_CLASSNAME}>{t('create_invitation_code')}</h2>
            <div className="flex flex-col gap-4">
              <div>
                <Label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                  {t('code')}
                </Label>
                <Input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  placeholder={t('invitation_code_placeholder')}
                />
              </div>
              <div>
                <Label className={ADMIN_FIELD_LABEL_CLASSNAME}>
                  {t('remaining_redeems')}
                </Label>
                <Input
                  type="number"
                  value={newRedeems}
                  onChange={(e) => setNewRedeems(parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
              <div>
                <Label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
                  <Checkbox
                    checked={newGrantsAdmin}
                    onCheckedChange={(checked) => setNewGrantsAdmin(checked === true)}
                  />
                  {t('grants_admin')}
                </Label>
              </div>
              <Button
                onClick={handleCreate}
                disabled={submitting || !newCode.trim()}
              >
                {submitting ? t('creating') : t('create')}
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <AdminLoadingState />
        ) : codes.length === 0 ? (
          <AdminEmptyState>{t('no_invitation_codes')}</AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table min-w-[700px]">
              <thead>
                <tr>
                  <th>{t('code')}</th>
                  <th>{t('remaining_redeems')}</th>
                  <th>{t('grants_admin')}</th>
                  <th>{t('created')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.code}>
                    <td className="font-mono font-semibold">{code.code}</td>
                    <td>
                      {editingCode === code.code ? (
                        <Input
                          type="number"
                          value={editRedeems}
                          onChange={(e) => setEditRedeems(parseInt(e.target.value) || 0)}
                          min="0"
                          className="w-20"
                        />
                      ) : (
                        code.remainingRedeems
                      )}
                    </td>
                    <td>
                      {editingCode === code.code ? (
                        <Checkbox
                          checked={editGrantsAdmin}
                          onCheckedChange={(checked) => setEditGrantsAdmin(checked === true)}
                        />
                      ) : (
                        code.grantsAdmin ? t('yes') : t('no')
                      )}
                    </td>
                    <td className={ADMIN_TABLE_CELL_MUTED_CLASSNAME}>
                      {new Date(code.createdAt).toLocaleString()}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        {editingCode === code.code ? (
                          <>
                            <Button
                              size="sm"
                              className="bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600"
                              onClick={() => handleUpdate(code.code)}
                              disabled={submitting}
                            >
                              {t('save')}
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => setEditingCode(null)}
                              disabled={submitting}
                            >
                              {t('cancel')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => startEdit(code)}
                              disabled={submitting}
                            >
                              {t('edit')}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(code.code)}
                              disabled={submitting}
                              className="group inline-flex items-center gap-1.5"
                            >
                              <RotatingDeleteIcon />
                              {t('delete')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}
      </div>
    </>
  );
};
